// Types and interfaces
import { PrayerName } from '../common/enums/PrayersName';
import { Status } from '../common/enums/Status';
import { WeekDays } from '../common/enums/WeekDays';
import { IMultiLanguageValue } from '../common/interfaces/MultiLanguageValue.interface';
import { IOption } from '../common/interfaces/Option.interface';

/**
 * Validates the value of a given field based on predefined rules.
 *
 * @param {string} field - The name of the field to validate.
 * @param {null | string | Status | boolean | PrayerName | WeekDays[] | IMultiLanguageValue[]} value - The value to validate.
 * @returns {string[]} An array of error messages, if any validation rules are violated.
 *
 * The function checks the validity of the value based on the field name:
 * - `title`:
 *   - Must not be empty.
 *   - Must not exceed 50 characters.
 *   - Must only contain alphanumeric characters and spaces.
 * - `description`:
 *   - Must not be empty.
 *   - Must not exceed 300 characters.
 * - `time`, `prayerName`, `adhanDelta`, `startDate`, `endDate`:
 *   - Must not be empty.
 * - `days`:
 *   - Must not be an empty array.
 *
 * Example usage:
 * ```
 * const errors = checkFieldValidity('title', 'Sample Title');
 * if (errors.length > 0) {
 *   console.log('Validation errors:', errors);
 * }
 * ```
 */
export const checkFieldValidity = (
  field: string,
  value:
    | null
    | string
    | Status
    | boolean
    | PrayerName
    | WeekDays[]
    | IMultiLanguageValue[]
    | IOption[]
) => {
  const errors: string[] = [];

  switch (field) {
    case 'title':
      if (!value) errors.push('titleRequired');
      if ((value as string).length > 50) errors.push('titleMaxLength');
      if (!/^[a-zA-Z0-9\u0600-\u06FF\s]*$/.test(value as string))
        errors.push('titleIncludes');
      break;

    case 'description':
      if (!value) errors.push('descriptionRequired');
      if ((value as string).length > 300) errors.push('descriptionMaxLength');
      break;

    case 'url':
      if (!value) {
        errors.push('urlRequired');
      } else {
        // URL validation regex
        const urlPattern =
          /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
        if (!urlPattern.test(value as string)) {
          errors.push('urlInvalid');
        }
      }
      break;

    case 'time':
    case 'prayerName':
    case 'startDate':
    case 'endDate':
    case 'sharingMessage':
    case 'markdownText':
    case 'notifyTitle':
    case 'notifyBody':
      if (!value) errors.push(`${field}Required`);
      break;

    case 'adhanDelta':
      if (!value) errors.push(`${field}Required`);
      if (parseInt(value as string) > 60 || parseInt(value as string) < -60)
        errors.push(`adhanMaxDelta`);
      break;

    case 'contentEngagementQuestions':
      if (!value) errors.push('contentEngagementQuestions');
      break;

    case 'days':
      if (!(value as WeekDays[]).length) errors.push('daysRequired');
      break;

    case 'answerOptions': {
      const options = value as IOption[];

      // At least two options required
      if (options.length < 2) {
        errors.push('atLeastTwoAnswerOptionsRequired');
      }

      // All option values must be non-empty and all sub-values must be non-empty
      const allValuesValid = options.every(
        (option) =>
          option.value.length > 0 &&
          option.value.every((v) => v.value.trim() !== '')
      );
      if (!allValuesValid) {
        errors.push('allAnswerOptionsMustHaveValues');
      }

      // Exactly one option must be marked as correct
      const correctCount = options.filter((option) => option.isCorrect).length;
      if (correctCount === 0) {
        errors.push('oneCorrectAnswerOptionRequired');
      } else if (correctCount > 1) {
        errors.push('onlyOneCorrectAnswerOptionAllowed');
      }
      break;
    }

    default:
      break;
  }

  return errors;
};
